import React, { useMemo } from 'react';

import SlotRow from './SlotRow';
import { ProvenanceChipLegend } from './ProvenanceChip';
import { BagSlot, BagSlotUpdate, DistanceBasis } from '../../types/bag.types';

export interface RackProps {
  slots: BagSlot[];
  onSlotChange: (slotOrder: number, patch: BagSlotUpdate) => void;
  onConfirm: (slotOrder: number) => void;
  onConfirmAll?: () => void;
  busy?: boolean;
  distanceBasis?: DistanceBasis;
}

/** The 14 includes the putter (MY_BAG_PLAN.md §4). The counter is wrong if it
 *  doesn't, so nothing is filtered out of it. */
const BAG_LIMIT = 14;

export default function Rack({
  slots,
  onSlotChange,
  onConfirm,
  onConfirmAll,
  busy = false,
  distanceBasis = 'carry',
}: RackProps): JSX.Element {
  const ordered = useMemo(
    () => slots.slice().sort((a, b) => a.slot_order - b.slot_order),
    [slots]
  );

  const inBagCount = ordered.filter((s) => s.in_bag).length;
  const benchedCount = ordered.length - inBagCount;
  /* Only a rung that actually holds a number can be confirmed — promoting an
     empty rung to `entered` would claim the golfer told us something they
     didn't. This matches what SlotRow's own Confirm button allows. */
  const estimatedCount = ordered.filter(
    (s) =>
      s.club_category !== 'putter' &&
      s.distance_provenance === 'estimated' &&
      s.carry_yards != null
  ).length;
  const missingCount = ordered.filter(
    (s) => s.club_category !== 'putter' && s.carry_yards == null
  ).length;

  if (ordered.length === 0) {
    return (
      <div className="rounded-brand border border-dashed border-line-2 bg-paper p-6 text-center">
        <h3 className="text-md font-semibold text-ink">Nothing in the rack yet</h3>
        <p className="mt-1 text-sm text-ink-2">
          Seed the ladder from your profile and confirm it a tap at a time, or start a bag from
          scratch. One number per club — carry — and nothing else.
        </p>
      </div>
    );
  }

  let countTone = 'text-ink-2';
  if (inBagCount > BAG_LIMIT) countTone = 'text-score-0';
  else if (inBagCount < BAG_LIMIT) countTone = 'text-ink-3';

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-brand border border-line-2 bg-paper p-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-xl tabular-nums text-ink">
              {inBagCount} / {BAG_LIMIT}
            </span>
            <span className={`text-sm ${countTone}`}>
              in the bag{benchedCount > 0 ? `, ${benchedCount} benched` : ''}
            </span>
          </div>

          {onConfirmAll && estimatedCount > 0 && (
            <button
              type="button"
              disabled={busy}
              onClick={onConfirmAll}
              className="min-h-[44px] rounded-brand bg-slate px-4 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              Confirm all {estimatedCount} estimated
            </button>
          )}
        </div>

        <p className="mt-2 text-xs text-ink-3">
          Distances are {distanceBasis === 'total' ? 'totals' : 'carries'} — your stock,
          solid-strike number, not your best one. The putter is one of the fourteen and holds no
          distance.
        </p>

        {estimatedCount > 0 && (
          <p className="mt-1 text-xs text-ink-2">
            {estimatedCount} {estimatedCount === 1 ? 'rung is' : 'rungs are'} still estimated. An
            estimated number can be gapped, but it can&apos;t be bent — confirm or correct it and
            bend advice unlocks.
          </p>
        )}

        {missingCount > 0 && (
          <p className="mt-1 text-xs text-ink-2">
            {missingCount} {missingCount === 1 ? 'rung has' : 'rungs have'} no carry yet. Fill the
            ones you know; the map draws around the rest.
          </p>
        )}

        <ProvenanceChipLegend className="mt-2" />
      </div>

      {ordered.map((slot) => (
        <SlotRow
          key={slot.id ?? slot.slot_order}
          slot={slot}
          onChange={(patch) => onSlotChange(slot.slot_order, patch)}
          onConfirm={() => onConfirm(slot.slot_order)}
          disabled={busy}
        />
      ))}
    </div>
  );
}
